import PropTypes from 'prop-types';
import React, {Children, Component} from 'react';
import {compose, identity} from '@republic/foundation/lang/function';
import {all, any, index, map, reduce} from '@republic/foundation/lang/array';
import {isArray, isEq, isEqual, isFunction} from '@republic/foundation/lang/is';
import {get, keys, owns} from '@republic/foundation/lang/object';
import {Stream} from '@republic/foundation/streams';
import {createMethod, createModel, on} from '@republic/foundation/streams/models';
import {createComponent} from './components';
import {createNamedContext, isContextual} from './context';
import {createEnhancer, subscribe, wrapped} from './enhancers';

const
    Dependency = class Dependency {
        constructor(stream, selector) {
            this.stream = stream;
            this.selector = selector;
        }
    },

    createDependency = (stream, selector) => new Dependency(stream, selector || null),

    isDependency = value => value instanceof Dependency,

    createStreamContext = (name, propTypes) => (
        createNamedContext(
            name,
            {
                ...propTypes || {},
                stream: PropTypes.instanceOf(Stream).isRequired
            })),

    error = (message, children) => {
        const
            [
                {
                    type: {
                        displayName: type
                    } = {},
                    _owner: {
                        _debugSource: {
                            fileName: filename,
                            lineNumber: line
                        } = {}
                    } = {}
                } = {}
            ] = Children.toArray(children),
            [name] = filename ? filename.split('/').slice(-1) : [];

        console.error(`${message}\n    in ${type || 'Unknown'} (at ${name || '?'}:${line || '?'})`);
        return null;
    },

    createStreamInterface = (name, context) => {
        const
            StreamInterface = (
                context.with('stream')(
                    createComponent(
                        name,
                        {
                            propTypes: {
                                stream: PropTypes.shape(context.types),
                                inherit: PropTypes.bool.isRequired
                            },
                            defaultProps: {
                                inherit: true
                            }
                        },
                        ({stream, inherit, children, ...props}) => {
                            if (!stream || !inherit) {
                                return (
                                    error(
                                        `${name} is an interface, so it must be rendered inside the context of ${context.name}`,
                                        children));
                            } else {
                                if (keys(props).length) {
                                    console.warn(`${name} inherits from a parent stream, so its props will be ignored`);
                                }
                                return children;
                            }
                        })));

        StreamInterface.context = context;
        StreamInterface.as = selector => createDependency(StreamInterface, selector);
        return StreamInterface;
    },

    createStream = (
        name,
        {
            enhance = null,
            singleton = false,
            compareDependencies = isEqual,
            compareStream = isEq,
            wrap = null,
            self = 'self',
            ...options
        },
        context,
        dependencies,
        methods,
        provide,
        initial,
        ...reducers) => {

        const
            names = dependencies ? keys(dependencies) : [],

            resolved = (
                reduce(
                    names,
                    (resolved, name) => {
                        const dependency = dependencies[name];

                        resolved[name] = (
                            isDependency(dependency) ?
                                dependency :
                                createDependency(dependency));
                        return resolved;
                    },
                    {})),

            contextual = (
                reduce(
                    names,
                    (contextual, name) => {
                        const stream = resolved[name].stream;

                        if (isContextual(stream)) {
                            contextual[name] = stream;
                        }
                        return contextual;
                    },
                    {})),

            createDependencies = (dependencies, props) => (
                reduce(
                    names,
                    (streams, name) => {
                        const
                            {stream, selector} = dependencies[name],
                            resolved = (
                                stream instanceof Stream ?
                                    stream :
                                    (stream.stream instanceof Stream ?
                                        stream.stream :
                                        null));

                        if (resolved) {
                            streams[name] = (selector || identity)(resolved, props);
                        } else {
                            console.error(
                                error(
                                `The dependency "${name}" is not a stream`));
                        }
                        return streams;
                    },
                    {})),

            createMethods = (methods, props) => {
                const
                    resolved = (
                        isFunction(methods) ?
                            methods(props) :
                            (methods || {}));

                return (
                    reduce(
                        keys(resolved),
                        (methods, name) => {
                            methods[name] = createMethod(resolved[name]);
                            return methods;
                        },
                        {}));
            },

            createStream = (dependencies, methods, props) => {
                const
                    streams = map(names, name => dependencies[name]),

                    register = (
                        Object.assign(
                            (stream, reducer) => (
                                on(stream, (stream, value) => {
                                    const data = reducer(stream.data, value, stream.dependencies);

                                    return (
                                        data === stream.data ?
                                            stream :
                                            {
                                                ...stream,
                                                data
                                            });
                                })),
                            {
                                dependencies: (
                                    reducer => {
                                        if (!streams.length) {
                                            throw new Error(`The stream "${name}" has no dependencies`);
                                        } else {
                                            return (
                                                on(Stream.combineAll(streams), (stream, streams) => {
                                                    const
                                                        dependencies = (
                                                            reduce(
                                                                streams,
                                                                (dependencies, stream, i) => {
                                                                    dependencies[names[i]] = stream;
                                                                    return dependencies;
                                                                },
                                                                {})),
                                                        data = reducer(stream.data, dependencies, stream.dependencies);

                                                    return (
                                                        (data === stream.data && compareDependencies(dependencies, stream.dependencies)) ?
                                                            stream :
                                                            {
                                                                data,
                                                                dependencies
                                                            });
                                                }));
                                        }
                                    })
                            })),

                    stream = (
                        createModel(
                            isFunction(initial) ?
                                () => ({
                                    data: initial(methods, props),
                                    ...streams.length ? {dependencies: {}} : {}
                                }) :
                                ({
                                    data: initial || {},
                                    ...streams.length ? {dependencies: {}} : {}
                                }),
                            ...map(reducers, reducer => reducer(register, methods, props))));

                return (
                    (wrap ?
                        wrap :
                        (dependencies, stream) => (stream.map(({data}) => data)))(
                        dependencies,
                        stream,
                        props)
                    .unique(compareStream)
                    .latest());
            },

            isStatic = (
                singleton &&
                all(names,
                    name => {
                        const {stream} = resolved[name];

                        return (
                            stream instanceof Stream ||
                            stream.stream instanceof Stream);
                    })),

            staticDependencies = (
                isStatic ?
                    createDependencies(
                        resolved,
                        reduce(
                            names,
                            (props, name) => {
                                props[name] = {
                                    ...resolved[name].stream.provide,
                                    stream: resolved[name].stream.stream
                                };
                                return props;
                            },
                            {})) :
                    null),

            staticProps = (
                isStatic ?
                    reduce(
                        names,
                        (props, name) => {
                            props[name] = {
                                ...resolved[name].stream.provide,
                                stream: staticDependencies[name],
                            };
                            return props;
                        },
                        {}) :
                    null),

            staticMethods = (
                isStatic ?
                    createMethods(methods, staticProps) :
                    null),

            staticStream = (
                isStatic ?
                    createStream(staticDependencies, staticMethods, staticProps) :
                    null),

            StreamProvider = (
                compose(
                    ...isArray(enhance) ?
                        enhance :
                        (enhance ? [enhance] : []),
                    ...map(
                        reduce(
                            keys(contextual),
                            (streams, name) => {
                                const stream = contextual[name];

                                if (index(streams, ({context}) => context === stream.context) < 0) {
                                    streams.push(stream);
                                }
                                return streams;
                            },
                            []),
                        stream => wrapped(stream, {inherit: true})),
                    ...reduce(
                        keys(contextual),
                        (contexts, name) => {
                            contexts.push(contextual[name].context.with(name));
                            return contexts;
                        },
                        []))(
                    createComponent(
                        name,
                        {
                            ...options || {},
                            propTypes: {
                                ...get(options, 'propTypes') || {},
                                ...reduce(
                                    keys(contextual),
                                    (propTypes, name) => {
                                        propTypes[name] = PropTypes.shape(contextual[name].context.types);
                                        return propTypes;
                                    },
                                    {})
                            }
                        },
                        class StreamProvider extends Component {
                            constructor(props) {
                                super(props);

                                const
                                    instanceMethods = (
                                        staticMethods ||
                                        createMethods(methods, props)),
                                    stream = (
                                        staticStream ||
                                        createStream(
                                            createDependencies(
                                                reduce(
                                                    names,
                                                    (streams, name) => {
                                                        if (owns(contextual, name)) {
                                                            streams[name] = (
                                                                createDependency(
                                                                    props[name],
                                                                    resolved[name].selector));
                                                        } else {
                                                            const {stream} = resolved[name];

                                                            streams[name] = (
                                                                createDependency(
                                                                    isFunction(stream) ?
                                                                        stream(props) :
                                                                        stream,
                                                                    resolved[name].selector));
                                                        }
                                                        return streams;
                                                    },
                                                    {}),
                                                props),
                                            instanceMethods,
                                            props));

                                this.provide = {
                                    ...isFunction(provide) ?
                                        provide(instanceMethods, stream, props) :
                                        (provide || {}),
                                    stream
                                };
                            }

                            render() {
                                const {children} = this.props;

                                return (
                                    <context.Provider value={this.provide}>
                                        {children}
                                    </context.Provider>);
                            }
                        }))),

            StreamComponent = (
                context.with(self)(
                    createComponent(
                        name,
                        {
                            propTypes: {
                                [self]: PropTypes.shape(context.types),
                                inherit: PropTypes.bool.isRequired
                            },
                            defaultProps: {
                                inherit: false
                            }
                        },
                        ({[self]: stream, inherit, children, ...props}) => {
                            if (singleton && stream && !inherit) {
                                return (
                                    error(
                                        `${name} must be a singleton, so it may not inherit from a parent stream`,
                                        children));
                            } else if (inherit && !stream && !isStatic) {
                                return (
                                    error(
                                        `${name} is dynamic, so it must have its inherit property set to false, or it must be rendered in the context of ${context.name}`,
                                        children));
                            } else if (!inherit || !stream) {
                                return (
                                    <StreamProvider {...props}>
                                        {children}
                                    </StreamProvider>);
                            } else {
                                if (keys(props).length) {
                                    console.warn(`${name} inherits from a parent stream, so its props will be ignored`);
                                }
                                return children;
                            }
                        })));

        StreamComponent.context = context;
        StreamComponent.as = selector => createDependency(StreamComponent, selector);
        if (isStatic) {
            StreamComponent.provide = (
                isFunction(provide) ?
                    provide(staticMethods, staticStream, staticProps) :
                    (provide || {}));
            StreamComponent.stream = staticStream;
        }
        return StreamComponent;
    },

    createKeyedStream = (
        name,
        {
            wrap = null,
            ...options
        },
        context,
        dependencies,
        keyed,
        methods,
        provide,
        initial,
        ...reducers) => (

        createStream(
            name,
            {
                ...options,
                wrap: (
                    wrap ||
                    ((dependencies, stream, props) => {
                        const names = keys(dependencies);

                        return (
                            Stream.combineAll([
                                stream,
                                ...map(names, name => dependencies[name])
                            ])
                            .map(
                                ([stream, ...streams]) => {
                                    const
                                        key = (
                                            keyed(
                                                reduce(
                                                    streams,
                                                    (depencencies, stream, i) => {
                                                        dependencies[names[i]] = stream;
                                                        return dependencies;
                                                    },
                                                    {}),
                                                props));

                                    return key ? stream.data[key] : null;
                                }));
                    }))
            },
            context,
            dependencies,
            methods,
            isFunction(provide) ?
                (methods, stream, props) => (
                    provide(methods, keyed, stream, props)) :
                provide,
            initial,
            ...map(
                reducers,
                reducer => (
                    (on, methods, props) => (
                        reducer(on, methods, keyed, props)))))),

    create = (StreamComponent, selector, options) => {
        const
            resolvedSelector = (
                isFunction(selector) ?
                    props => ({
                        ...selector(props)
                    }) :
                    () => (selector || {}));

        return (
            createEnhancer(
                WrappedComponent => (
                    `Create${
                        StreamComponent.displayName ||
                        StreamComponent.name ||
                        'Component'
                    }(${
                        WrappedComponent.displayName ||
                        WrappedComponent.name ||
                        'Component'
                    })`,
                    options,
                    ({children, ...props}) => (
                        <StreamComponent {...resolvedSelector(props)}>
                            <WrappedComponent {...{children, ...props}} />
                        </StreamComponent>))));
    },

    use = (dependencies, props, options) => {
        const
            names = keys(dependencies),

            resolved = (
                reduce(
                    names,
                    (resolved, name) => {
                        const dependency = dependencies[name];

                        resolved[name] = (
                            isDependency(dependency) ?
                                dependency :
                                createDependency(dependency));
                        return resolved;
                    },
                    {})),

            contextual = (
                reduce(
                    names,
                    (contextual, name) => {
                        const stream = resolved[name].stream;

                        if (isContextual(stream)) {
                            contextual[name] = stream;
                        }
                        return contextual;
                    },
                    {}));

        return (
            compose(
                ...map(
                    reduce(
                        keys(contextual),
                        (streams, name) => {
                            const stream = contextual[name];

                            if (index(streams, ({context}) => context === stream.context) < 0) {
                                streams.push(stream);
                            }
                            return streams;
                        },
                        []),
                    stream => wrapped(stream, {inherit: true})),
                ...reduce(
                    keys(contextual),
                    (contexts, name) => {
                        contexts.push(contextual[name].context.with(name));
                        return contexts;
                    },
                    []),
                subscribe(
                    props => ([
                        props,
                        reduce(
                            names,
                            (streams, name) => {
                                const
                                    stream = (
                                        owns(contextual, name) ?
                                            props[name] :
                                            isFunction(resolved[name].stream) ?
                                                resolved[name].stream.call(undefined, props) :
                                                resolved[name].stream);

                                if (stream instanceof Stream) {
                                    streams[name] = stream;
                                } else if (stream.stream instanceof Stream) {
                                    streams[name] = stream.stream;
                                } else {
                                    throw (
                                        new Error(
                                            `The dependecny "${name}" is not a stream`));
                                }
                                return streams;
                            },
                            {})
                    ]),
                    ([props, streams]) => (
                        Stream
                        .combineAll(
                            map(names,
                                name => (
                                    (resolved[name].selector || identity)(
                                        streams[name],
                                        props))))
                        .map(
                            streams => (
                                reduce(
                                    streams,
                                    (values, stream, i) => {
                                        values[names[i]] = stream;
                                        return values;
                                    },
                                    {})))),
                    props,
                    {
                        ...options || {},
                        propTypes: {
                            ...reduce(
                                keys(contextual),
                                (propTypes, name) => {
                                    propTypes[name] = (
                                        PropTypes.shape(
                                            contextual[name].context.types));
                                    return propTypes;
                                },
                                {}),
                            ...(options && options.propTypes) ?
                                options.propTypes :
                                {}
                        }
                    })));
    };

export {
    createStreamContext,
    createStreamInterface,
    createStream,
    createKeyedStream,
    create,
    use
};
